/**
 * Build: blocks you stack up, seen from the corner.
 *
 * The whole point is the turn button. A child builds something from one side,
 * turns it, and finds it is the same thing seen differently — which is where
 * thinking in three dimensions starts, and is exactly what a CAD program asks
 * of you every day.
 *
 * There is no 3-D engine underneath: an isometric grid is arithmetic, and each
 * cube is three parallelograms drawn back to front. See src/lib/voxel.ts. The
 * model is kept separate from the drawing, so a real 3-D renderer could take
 * it over later without any of this being rewritten.
 */

import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { Boxes, Eraser, Hammer, Home, RotateCw, Trash2 } from 'lucide-preact';
import type { Camera } from '../lib/scene.ts';
import { toScreen, toWorld } from '../lib/scene.ts';
import { pinch, zoomAt } from '../lib/gesture.ts';
import type { Block, Model, Rotation, Tile } from '../lib/voxel.ts';
import {
  addBlock,
  decodeModel,
  encodeModel,
  extentOf,
  GROUND,
  inBounds,
  MAX_HEIGHT,
  drawOrderFree,
  groundCellFree,
  nearestRotation,
  projectFree,
  removeBlock,
  topOf,
} from '../lib/voxel.ts';
import type { Spark } from '../engine/render.ts';
import { drawSparks, spawnSparks, updateSparks } from '../engine/render.ts';
import { token } from '../engine/palette.ts';
import { play, playNote } from '../engine/audio.ts';
import { isTap, useSurface } from '../hooks/useSurface.ts';
import { Chip, Coach, ConfirmSheet, CrayonRow, ToyButton } from '../ui/controls.tsx';

interface Props {
  onExit: () => void;
}

const TILE: Tile = { halfW: 30, halfD: 16, rise: 34 };
const STORAGE_KEY = 'imagineplay.blocks';

interface Gesture {
  a: number;
  b: number;
  prevA: { x: number; y: number };
  prevB: { x: number; y: number };
}

/** Lighten or darken a colour for the three faces of a cube. */
function shade(color: string, amount: number): string {
  return `color-mix(in srgb, ${color} ${Math.round((1 - Math.abs(amount)) * 100)}%, ${amount > 0 ? '#ffffff' : '#241d3a'})`;
}

export function VoxelLab({ onExit }: Props): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const modelRef = useRef<Model>(new Map());
  const camRef = useRef<Camera>({ x: 0, y: 0, scale: 1 });
  const pointsRef = useRef(new Map<number, { x: number; y: number }>());
  const gestureRef = useRef<Gesture | null>(null);
  const sparksRef = useRef<Spark[]>([]);
  /** Screen outlines of each drawn cube, newest last, for picking. */
  const hitsRef = useRef<Array<{ block: Block; path: Path2D }>>([]);
  const saveRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Turned freely, in radians — a drag moves this, the button snaps it. */
  const angleRef = useRef(0);

  const [rotation, setRotation] = useState<Rotation>(0);
  const [color, setColor] = useState(() => token('--crayon-red'));
  const [taking, setTaking] = useState(false);
  const [count, setCount] = useState(0);
  const [askClear, setAskClear] = useState(false);

  const live = useRef({ color, taking });
  live.current = { color, taking };

  /** What was built last time comes back. */
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        modelRef.current = decodeModel(JSON.parse(raw));
        setCount(modelRef.current.size);
      }
    } catch {
      // A model that will not parse is one lost build, not a broken room.
    }
  }, []);

  const remember = useCallback(() => {
    if (saveRef.current) clearTimeout(saveRef.current);
    saveRef.current = setTimeout(() => {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(encodeModel(modelRef.current)));
      } catch {
        // Out of room: the build is still on screen, it just will not persist.
      }
    }, 500);
  }, []);

  const changed = useCallback(() => {
    setCount(modelRef.current.size);
    remember();
  }, [remember]);

  useSurface(canvasRef, {
    onFrame: (ctx, dt, { width, height }) => {
      updateSparks(sparksRef.current, dt);
      const cam = camRef.current;
      const angle = angleRef.current;

      ctx.fillStyle = token('--paper');
      ctx.fillRect(0, 0, width, height);

      const at = (cell: { x: number; y: number; z: number }) => {
        const p = projectFree(cell, angle, TILE);
        return toScreen(cam, p.x, p.y, width, height);
      };

      // The ground, so there is something to build ON rather than in mid-air.
      ctx.save();
      ctx.strokeStyle = token('--paper-line');
      ctx.lineWidth = 1.5;
      for (let x = -7; x <= 7; x++) {
        const from = at({ x, y: GROUND - 0.5, z: -7 });
        const to = at({ x, y: GROUND - 0.5, z: 7 });
        ctx.beginPath();
        ctx.moveTo(from.x, from.y);
        ctx.lineTo(to.x, to.y);
        ctx.stroke();
      }
      for (let z = -7; z <= 7; z++) {
        const from = at({ x: -7, y: GROUND - 0.5, z });
        const to = at({ x: 7, y: GROUND - 0.5, z });
        ctx.beginPath();
        ctx.moveTo(from.x, from.y);
        ctx.lineTo(to.x, to.y);
        ctx.stroke();
      }
      ctx.restore();

      // Back to front, so near blocks cover far ones.
      const hits: Array<{ block: Block; path: Path2D }> = [];
      const hw = TILE.halfW * cam.scale;
      const hd = TILE.halfD * cam.scale;
      const rise = TILE.rise * cam.scale;

      for (const block of drawOrderFree([...modelRef.current.values()], angle)) {
        const c = at(block);
        // Top face, then the two sides that can be seen from this corner.
        const top = new Path2D();
        top.moveTo(c.x, c.y - hd);
        top.lineTo(c.x + hw, c.y);
        top.lineTo(c.x, c.y + hd);
        top.lineTo(c.x - hw, c.y);
        top.closePath();

        const left = new Path2D();
        left.moveTo(c.x - hw, c.y);
        left.lineTo(c.x, c.y + hd);
        left.lineTo(c.x, c.y + hd + rise);
        left.lineTo(c.x - hw, c.y + rise);
        left.closePath();

        const right = new Path2D();
        right.moveTo(c.x + hw, c.y);
        right.lineTo(c.x, c.y + hd);
        right.lineTo(c.x, c.y + hd + rise);
        right.lineTo(c.x + hw, c.y + rise);
        right.closePath();

        ctx.lineWidth = Math.max(1.5, 2.5 * cam.scale);
        ctx.strokeStyle = 'rgba(44, 37, 69, 0.85)';
        ctx.lineJoin = 'round';
        for (const [path, tint] of [
          [left, -0.28],
          [right, -0.14],
          [top, 0.14],
        ] as const) {
          ctx.fillStyle = shade(block.color, tint);
          ctx.fill(path);
          ctx.stroke(path);
        }

        // The whole cube, for picking: top plus both sides.
        const whole = new Path2D();
        whole.addPath(top);
        whole.addPath(left);
        whole.addPath(right);
        hits.push({ block, path: whole });
      }
      hitsRef.current = hits;

      drawSparks(ctx, sparksRef.current);
    },

    onDown: (p) => {
      pointsRef.current.set(p.id, { x: p.pos.x, y: p.pos.y });
      if (pointsRef.current.size === 2) {
        const [a, b] = [...pointsRef.current.entries()];
        gestureRef.current = { a: a[0], b: b[0], prevA: { ...a[1] }, prevB: { ...b[1] } };
      }
    },

    onMove: (p, { width, height }) => {
      const previous = pointsRef.current.get(p.id);
      if (!previous) return;
      const next = { x: p.pos.x, y: p.pos.y };
      const g = gestureRef.current;

      if (g) {
        pointsRef.current.set(p.id, next);
        const a = pointsRef.current.get(g.a);
        const b = pointsRef.current.get(g.b);
        if (a && b) {
          camRef.current = pinch(camRef.current, g.prevA, g.prevB, a, b, width, height);
          g.prevA = { ...a };
          g.prevB = { ...b };
        }
        return;
      }

      // One finger turns the model — the thing this room is for. Two fingers
      // move and zoom, which is where panning went.
      angleRef.current += (next.x - previous.x) * 0.009;
      setRotation(nearestRotation(angleRef.current));
      pointsRef.current.set(p.id, next);
    },

    onUp: (p, { width, height }) => {
      pointsRef.current.delete(p.id);
      if (gestureRef.current) {
        if (pointsRef.current.size < 2) gestureRef.current = null;
        return;
      }
      if (!isTap(p)) return;

      const { color: c, taking } = live.current;

      // The topmost cube under the finger, if any.
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      let hit: Block | null = null;
      if (ctx) {
        for (let i = hitsRef.current.length - 1; i >= 0; i--) {
          if (ctx.isPointInPath(hitsRef.current[i].path, p.pos.x, p.pos.y)) {
            hit = hitsRef.current[i].block;
            break;
          }
        }
      }

      if (taking) {
        if (!hit) return;
        modelRef.current = removeBlock(modelRef.current, hit);
        spawnSparks(sparksRef.current, { x: p.pos.x, y: p.pos.y }, [hit.color], 8, 220);
        play('pop', 0.5);
        changed();
        return;
      }

      // Tapping a block stacks one on top; tapping the ground starts a column.
      let target: { x: number; y: number; z: number };
      if (hit) {
        target = { x: hit.x, y: topOf(modelRef.current, hit.x, hit.z) + 1, z: hit.z };
      } else {
        const world = toWorld(camRef.current, p.pos.x, p.pos.y, width, height);
        const cell = groundCellFree(world.x, world.y, angleRef.current, TILE);
        target = { x: cell.x, y: topOf(modelRef.current, cell.x, cell.z) + 1, z: cell.z };
        if (target.y < GROUND) target.y = GROUND;
      }

      if (!inBounds(target)) {
        play('thud', 0.4);
        return;
      }

      modelRef.current = addBlock(modelRef.current, { ...target, color: c });
      // Higher blocks ring higher, so a tower is a rising scale.
      playNote(Math.min(12, target.y + 2), { duration: 0.22, gain: 0.2 });
      spawnSparks(sparksRef.current, { x: p.pos.x, y: p.pos.y }, [c], 6, 180);
      changed();
    },

    onWheel: (w, { width, height }) => {
      camRef.current = zoomAt(camRef.current, Math.exp(-w.deltaY * 0.0016), w.pos.x, w.pos.y, width, height);
    },
  });

  /** The button snaps square-on, for a clean view of a side. */
  const spin = () => {
    const next = ((nearestRotation(angleRef.current) + 1) % 4) as Rotation;
    angleRef.current = (next * Math.PI) / 2;
    setRotation(next);
    play('whoosh', 0.5);
  };

  const clear = () => {
    modelRef.current = new Map();
    camRef.current = { x: 0, y: 0, scale: 1 };
    angleRef.current = 0;
    setRotation(0);
    setAskClear(false);
    changed();
    play('whoosh', 0.6);
  };

  const size = extentOf(modelRef.current);
  const facing = ['front', 'right', 'back', 'left'][rotation];

  return (
    <div class="screen">
      <div class="stage">
        <canvas ref={canvasRef} aria-label="Block builder" />
        <div class="topbar">
          <ToyButton round label="Home" hideLabel onClick={onExit}>
            <Home size={24} strokeWidth={2.5} />
          </ToyButton>
          <div class="topbar__group">
            <ToyButton round label="Turn it round" hideLabel onClick={spin}>
              <RotateCw size={24} strokeWidth={2.5} />
            </ToyButton>
            <ToyButton round label="Knock it all down" hideLabel disabled={count === 0} onClick={() => setAskClear(true)}>
              <Trash2 size={24} strokeWidth={2.5} />
            </ToyButton>
          </div>
        </div>

        <p class="readout">
          <strong>
            {count} block{count === 1 ? '' : 's'}
          </strong>
          <span>
            {count === 0 ? 'tap the ground to start' : `${size.width} across, ${size.depth} back, ${size.height} up`}
          </span>
        </p>

        <Coach>
          {count === 0
            ? 'Tap to put a block down. Tap a block to stack another on top'
            : `Looking at the ${facing}. Drag to turn it round, two fingers to move`}
        </Coach>
      </div>

      <div class="dock">
        <CrayonRow value={color} onChange={setColor} />
        <div class="dock__row">
          <Chip label="Build" pressed={!taking} onClick={() => setTaking(false)}>
            <Hammer size={20} strokeWidth={2.75} />
          </Chip>
          <Chip label="Take away" pressed={taking} onClick={() => setTaking(true)}>
            <Eraser size={20} strokeWidth={2.75} />
          </Chip>
          <div class="dock__spacer" />
          <Chip label={`${MAX_HEIGHT} high`} onClick={spin}>
            <Boxes size={20} strokeWidth={2.75} />
          </Chip>
        </div>
      </div>

      {askClear && (
        <ConfirmSheet title="Knock the whole thing down?" confirmLabel="Knock it down" onConfirm={clear} onCancel={() => setAskClear(false)} />
      )}
    </div>
  );
}
